import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import NumericInputField from "../common/NumericInputField";
import CustomAutoComplete from "../common/CustomAutoComplete";
import IOSSwitch from "../common/IOSSwitch";
import {
  convertRange,
  formatConvertedValue,
  requiresConversion,
} from "../../../../common/utils/unitConverter";
import { ServiceAttribute } from "./types";

// Constants
const TYPING_DELAY_MS = 800;

type Props = {
  attribute: ServiceAttribute;
  onAttributeChange: (updatedAttribute: ServiceAttribute) => void;
};

const NumberAndRangeElement = ({ attribute, onAttributeChange }: Props) => {
  const theme: Theme = useTheme();
  // Set default to range mode if attribute type is in range types
  const [isRangeMode, setIsRangeMode] = useState(attribute.type === 1);

  // State to track if user is currently typing (to delay validation)
  const [isTyping, setIsTyping] = useState({
    single: false,
    min: false,
    max: false,
  });
  const typingTimeoutRef = useRef<{
    single?: NodeJS.Timeout;
    min?: NodeJS.Timeout;
    max?: NodeJS.Timeout;
  }>({});

  // Determine if this attribute requires unit conversion
  const needsConversion = useMemo(
    () => requiresConversion(attribute.unitList || []),
    [attribute.unitList]
  );

  // Get the current selected unit or default to defaultUnit
  const currentUnit = useMemo(
    () => attribute.unitValue || attribute.defaultUnit || "",
    [attribute.unitValue, attribute.defaultUnit]
  );

  // Check if "one-off" unit is selected
  const isOneOffMode = useMemo(
    () => currentUnit.toLowerCase() === "one-off",
    [currentUnit]
  );

  // Calculate the converted range for the currently selected unit
  const convertedRange = useMemo(() => {
    if (
      !needsConversion ||
      !currentUnit ||
      currentUnit === attribute.defaultUnit
    ) {
      return { min: attribute.min, max: attribute.max };
    }

    // Convert string min/max to numbers for conversion
    const converted = convertRange(
      parseFloat(attribute.min),
      parseFloat(attribute.max),
      attribute.defaultUnit,
      currentUnit
    );

    return {
      min: String(formatConvertedValue(converted.min)),
      max: String(formatConvertedValue(converted.max)),
    };
  }, [
    needsConversion,
    currentUnit,
    attribute.min,
    attribute.max,
    attribute.defaultUnit,
    attribute.unitList,
    attribute.unitValue,
  ]);

  // Display min as "1" instead of "0" in UI since 0 is not allowed
  const displayMin = useMemo(() => {
    return String(convertedRange.min) === "0" ? "1" : convertedRange.min;
  }, [convertedRange.min]);

  // Memoized validation functions
  const validateSingleValue = useCallback(
    (value: number, hasUserInput: boolean): string => {
      if (!hasUserInput && value === 0) return "";

      // Skip range validation if both min and max are "0" (unrestricted)
      const { min, max } = convertedRange;
      // Convert to string for comparison since min/max come as strings from API
      const minStr = String(min);
      const maxStr = String(max);

      if (minStr === "0" && maxStr === "0") {
        // Only validate that value is not null
        if (value === null || value === undefined) {
          return "Value is required";
        }
        return "";
      }

      // Normal range validation
      const minNum = parseFloat(min);
      const maxNum = parseFloat(max);
      // Display min as "1" instead of "0" in UI since 0 is not allowed
      const displayMin = String(min) === "0" ? "1" : min;

      if (value < minNum || value > maxNum) {
        return `Value must be between ${displayMin} and ${max} ${currentUnit.trim()}`;
      }
      return "";
    },
    [convertedRange, currentUnit]
  );

  const validateMinValue = useCallback(
    (
      minValue: number,
      maxValue: number | undefined,
      hasUserInput: boolean
    ): string => {
      if (!hasUserInput && minValue === 0) return "";

      // Skip range validation if both min and max are "0" (unrestricted)
      const { min, max } = convertedRange;
      // Convert to string for comparison since min/max come as strings from API
      const minStr = String(min);
      const maxStr = String(max);

      if (minStr === "0" && maxStr === "0") {
        // Only validate that min is not null and min < max
        if (minValue === null || minValue === undefined) {
          return "Min value is required";
        }
        if (
          maxValue !== null &&
          maxValue !== undefined &&
          minValue >= maxValue
        ) {
          return "Min value must be less than max value";
        }
        return "";
      }

      // Normal range validation
      const minNum = parseFloat(min);
      const maxNum = parseFloat(max);
      // Display min as "1" instead of "0" in UI since 0 is not allowed
      const displayMin = String(min) === "0" ? "1" : min;

      if (minValue < minNum || minValue > maxNum) {
        return `Min value must be between ${displayMin} and ${max} ${currentUnit.trim()}`;
      }
      if (maxValue && maxValue > 0 && minValue >= maxValue) {
        return "Min value must be less than max value";
      }
      return "";
    },
    [convertedRange, currentUnit]
  );

  const validateMaxValue = useCallback(
    (
      maxValue: number,
      minValue: number | undefined,
      hasUserInput: boolean
    ): string => {
      if (!hasUserInput && maxValue === 0) return "";

      // Skip range validation if both min and max are "0" (unrestricted)
      const { min, max } = convertedRange;
      // Convert to string for comparison since min/max come as strings from API
      const minStr = String(min);
      const maxStr = String(max);

      if (minStr === "0" && maxStr === "0") {
        // Only validate that max is not null
        if (maxValue === null || maxValue === undefined) {
          return "Max value is required";
        }
        return "";
      }

      // Normal range validation
      const minNum = parseFloat(min);
      const maxNum = parseFloat(max);
      // Display min as "1" instead of "0" in UI since 0 is not allowed
      const displayMin = String(min) === "0" ? "1" : min;

      if (maxValue < minNum || maxValue > maxNum) {
        return `Max value must be between ${displayMin} and ${max} ${currentUnit.trim()}`;
      }
      return "";
    },
    [convertedRange, currentUnit]
  );

  // Function to handle typing delay
  const handleTypingDelay = (field: "single" | "min" | "max") => {
    setIsTyping((prev) => ({ ...prev, [field]: true }));

    // Clear existing timeout
    if (typingTimeoutRef.current[field]) {
      clearTimeout(typingTimeoutRef.current[field]);
    }

    // Set new timeout to stop "typing" state after delay
    typingTimeoutRef.current[field] = setTimeout(() => {
      setIsTyping((prev) => ({ ...prev, [field]: false }));
    }, TYPING_DELAY_MS);
  };

  // Memoized user input checks
  const hasUserInputSingle = useMemo(
    () => attribute.value && attribute.value !== 0,
    [attribute.value]
  );
  const hasUserInputMin = useMemo(
    () => attribute.minValue && attribute.minValue !== 0,
    [attribute.minValue]
  );
  const hasUserInputMax = useMemo(
    () => attribute.maxValue && attribute.maxValue !== 0,
    [attribute.maxValue]
  );

  // Memoized error calculations
  const errorMessages = useMemo(() => {
    const errors = [];

    // Check if any field is being typed in (for range mode, don't show errors while typing in either field)
    const isAnyFieldTyping = isTyping.single || isTyping.min || isTyping.max;

    // Single mode error
    if (!isRangeMode && !isAnyFieldTyping) {
      const error = validateSingleValue(
        attribute.value || 0,
        !!hasUserInputSingle
      );
      if (error) errors.push(error);
    }

    // Range mode errors - only show when neither min nor max is being typed
    if (isRangeMode && !isAnyFieldTyping) {
      const minError = validateMinValue(
        attribute.minValue || 0,
        attribute.maxValue,
        !!hasUserInputMin
      );
      if (minError) errors.push(minError);

      const maxError = validateMaxValue(
        attribute.maxValue || 0,
        attribute.minValue,
        !!hasUserInputMax
      );
      if (maxError) errors.push(maxError);
    }

    return errors;
  }, [
    isRangeMode,
    isTyping,
    attribute.value,
    attribute.minValue,
    attribute.maxValue,
    hasUserInputSingle,
    hasUserInputMin,
    hasUserInputMax,
    validateSingleValue,
    validateMinValue,
    validateMaxValue,
  ]);

  const hasErrors = errorMessages.length > 0;
  const combinedErrorMessage = errorMessages.join(" and ");

  // Check if any field is being typed in
  const isAnyFieldTyping = isTyping.single || isTyping.min || isTyping.max;

  // Individual error states for input field styling - don't show errors while typing in any field
  const singleValueError =
    !isRangeMode && !isAnyFieldTyping
      ? validateSingleValue(attribute.value || 0, !!hasUserInputSingle)
      : "";
  const minValueError =
    isRangeMode && !isAnyFieldTyping
      ? validateMinValue(
          attribute.minValue || 0,
          attribute.maxValue,
          !!hasUserInputMin
        )
      : "";
  const maxValueError =
    isRangeMode && !isAnyFieldTyping
      ? validateMaxValue(
          attribute.maxValue || 0,
          attribute.minValue,
          !!hasUserInputMax
        )
      : "";

  // Initialize mode based on existing values (only on mount)
  useEffect(() => {
    // Check if user has already entered values (only on mount)
    const hasSingleValue =
      attribute.value !== null &&
      attribute.value !== undefined &&
      attribute.value !== 0;
    const hasRangeValues =
      (attribute.minValue !== null &&
        attribute.minValue !== undefined &&
        attribute.minValue !== 0) ||
      (attribute.maxValue !== null &&
        attribute.maxValue !== undefined &&
        attribute.maxValue !== 0);

    // If values exist, determine mode from what's filled
    if (hasSingleValue) {
      setIsRangeMode(false);
    } else if (hasRangeValues) {
      setIsRangeMode(true);
    } else {
      // No values exist, use default based on attribute type
      // Type 1 defaults to range, Type 3 defaults to single
      setIsRangeMode(attribute.type === 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Set default unit when unit-related properties change
  useEffect(() => {
    // Set default unit if not already set (trim to handle spaces)
    if (!attribute.unitValue || attribute.unitValue.trim() === "") {
      let unitToSet: string | null = null;

      // For type 1 (range with unitList): use defaultUnit if available in unitList
      if (attribute.type === 1) {
        if (
          attribute.unitList &&
          attribute.defaultUnit &&
          attribute.defaultUnit.trim() !== "" &&
          attribute.unitList.includes(attribute.defaultUnit.trim())
        ) {
          // Default unit is in the list - use it
          unitToSet = attribute.defaultUnit.trim();
        } else if (
          attribute.defaultUnit &&
          attribute.defaultUnit.trim() !== ""
        ) {
          // unitList is null/empty, but defaultUnit exists - use defaultUnit
          unitToSet = attribute.defaultUnit.trim();
        } else if (attribute.unit && attribute.unit.trim() !== "") {
          // Fall back to unit if no defaultUnit
          unitToSet = attribute.unit.trim();
        }
      }
      // For type 3 (single unit): use attribute.unit or defaultUnit
      else if (attribute.type === 3) {
        if (attribute.unit && attribute.unit.trim() !== "") {
          unitToSet = attribute.unit.trim();
        } else if (
          attribute.defaultUnit &&
          attribute.defaultUnit.trim() !== ""
        ) {
          // Fall back to defaultUnit if unit is empty
          unitToSet = attribute.defaultUnit.trim();
        }
      }

      if (unitToSet) {
        onAttributeChange({
          ...attribute,
          unitValue: unitToSet,
        });
      }
    }
  }, [
    attribute.unitValue,
    attribute.defaultUnit,
    attribute.unitList,
    attribute.type,
    attribute.unit,
    attribute.name,
    onAttributeChange,
  ]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(typingTimeoutRef.current).forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  // Handle one-off mode
  useEffect(() => {
    if (isOneOffMode) {
      // Set value to 1 and switch to single mode
      if (attribute.value !== 1 || isRangeMode) {
        setIsRangeMode(false);
        onAttributeChange({
          ...attribute,
          value: 1,
          minValue: 0,
          maxValue: 0,
        });
      }
    }
  }, [isOneOffMode, attribute, isRangeMode, onAttributeChange]);

  // Memoized change handlers - react-number-format handles all parsing
  const handleSingleValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleTypingDelay("single");
      const value = e.target.value;
      // react-number-format gives us clean numeric strings or empty
      const numericValue = value === "" ? 0 : parseFloat(value);
      onAttributeChange({
        ...attribute,
        value: numericValue,
      });
    },
    [attribute, onAttributeChange]
  );

  const handleMinValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleTypingDelay("min");
      const value = e.target.value;
      // react-number-format gives us clean numeric strings or empty
      const numericValue = value === "" ? 0 : parseFloat(value);
      onAttributeChange({
        ...attribute,
        minValue: numericValue,
      });
    },
    [attribute, onAttributeChange]
  );

  const handleMaxValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleTypingDelay("max");
      const value = e.target.value;
      // react-number-format gives us clean numeric strings or empty
      const numericValue = value === "" ? 0 : parseFloat(value);
      onAttributeChange({
        ...attribute,
        maxValue: numericValue,
      });
    },
    [attribute, onAttributeChange]
  );

  const handleUnitChange = useCallback(
    (value: string | null) => {
      // When clearing (value is null), set to defaultUnit instead of empty string
      const newUnitValue = value || attribute.defaultUnit || "";
      onAttributeChange({
        ...attribute,
        unitValue: newUnitValue,
      });
    },
    [attribute, onAttributeChange]
  );

  const handleToggleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsRangeMode(event.target.checked);
      // Reset value fields when switching modes, but preserve unit
      onAttributeChange({
        ...attribute,
        value: 0,
        minValue: 0,
        maxValue: 0,
        // Keep unitValue as is, don't reset it
      });
    },
    [attribute, onAttributeChange]
  );

  const renderUnitComponent = useMemo(
    () => (
      <Box sx={styles.inputWrapper}>
        <CustomAutoComplete
          label="Unit"
          options={
            attribute.unitList && attribute.unitList.length > 0
              ? attribute.unitList
              : [attribute.unit]
          }
          value={attribute.unitValue ?? attribute.defaultUnit}
          onChange={handleUnitChange}
          placeholder="Select unit"
        />
      </Box>
    ),
    [
      attribute.unitList,
      attribute.unitValue,
      handleUnitChange,
      attribute.type,
      attribute.unit,
    ]
  );

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerContainer}>
        <Box style={styles.title(theme)}>{attribute.name}</Box>
        <IOSSwitch
          checked={isRangeMode}
          onChange={handleToggleChange}
          leftLabel="Single"
          rightLabel="Range"
          showLabels={true}
          disabled={isOneOffMode}
        />
      </Box>
      <Box sx={styles.inputContainer}>
        {!isRangeMode ? (
          // Single mode
          <>
            <Box sx={styles.inputWrapper}>
              <NumericInputField
                label="Value"
                value={attribute.value?.toString() || ""}
                handleChange={handleSingleValueChange}
                name="singleValue"
                error={singleValueError ? "error" : ""}
                disabled={isOneOffMode}
              />
            </Box>
            {renderUnitComponent}
          </>
        ) : (
          // Range mode
          <>
            <Box sx={styles.inputWrapper}>
              <NumericInputField
                label="Min"
                value={attribute.minValue?.toString() || ""}
                handleChange={handleMinValueChange}
                name="minValue"
                error={minValueError ? "error" : ""}
              />
            </Box>
            <Box sx={styles.inputWrapper}>
              <NumericInputField
                label="Max"
                value={attribute.maxValue?.toString() || ""}
                handleChange={handleMaxValueChange}
                name="maxValue"
                error={maxValueError ? "error" : ""}
              />
            </Box>
            {renderUnitComponent}
          </>
        )}
      </Box>
      {hasErrors ? (
        <Box sx={styles.errorText(theme)}>{combinedErrorMessage}</Box>
      ) : !isOneOffMode &&
        (String(convertedRange.min) !== "0" ||
          String(convertedRange.max) !== "0") ? (
        <Box sx={styles.rangeText(theme)}>
          {`Allowed range: ${displayMin}–${
            convertedRange.max
          } ${currentUnit.trim()}`}
        </Box>
      ) : !isOneOffMode &&
        attribute.type === 3 &&
        attribute.text &&
        attribute.text.trim() !== "" ? (
        <Box sx={styles.rangeText(theme)}>
          {`Recommended value: ${attribute.text} ${attribute.defaultUnit}`}
        </Box>
      ) : null}
    </Box>
  );
};

// Validation function that can be called from parent components
export const validateDurationAttribute = (
  attribute: ServiceAttribute
): string => {
  // Determine if this attribute requires unit conversion
  const needsConversion = requiresConversion(attribute.unitList || []);

  // Determine actual mode based on which fields have values, not just type
  const hasRangeValuesSet =
    (attribute.minValue !== null &&
      attribute.minValue !== undefined &&
      attribute.minValue !== 0) ||
    (attribute.maxValue !== null &&
      attribute.maxValue !== undefined &&
      attribute.maxValue !== 0);

  const hasSingleValueSet =
    attribute.value !== null &&
    attribute.value !== undefined &&
    attribute.value !== 0;

  // Use range mode if range values are set, otherwise use single mode
  const isRangeMode = hasRangeValuesSet;

  // Check if values are entered
  const hasSingleValue = hasSingleValueSet && !isRangeMode;
  const hasRangeValues = hasRangeValuesSet;

  // If values are entered, check if unit is selected (for multi-unit attributes)
  if (
    (hasSingleValue || hasRangeValues) &&
    attribute.unitList &&
    attribute.unitList.length > 1
  ) {
    if (!attribute.unitValue || attribute.unitValue.trim() === "") {
      return "Please select a unit";
    }
  }

  const currentUnit = attribute.unitValue || attribute.defaultUnit || "";

  // Calculate the converted range for the currently selected unit
  let convertedRange = { min: attribute.min, max: attribute.max };

  if (needsConversion && currentUnit && currentUnit !== attribute.defaultUnit) {
    // Convert string min/max to numbers for conversion
    const converted = convertRange(
      parseFloat(attribute.min),
      parseFloat(attribute.max),
      attribute.defaultUnit,
      currentUnit
    );
    convertedRange = {
      min: String(formatConvertedValue(converted.min)),
      max: String(formatConvertedValue(converted.max)),
    };
  }

  const { min, max } = convertedRange;

  // Display min as "1" instead of "0" in UI since 0 is not allowed
  const displayMin = String(min) === "0" ? "1" : min;

  // Skip range validation if both min and max are "0" (unrestricted)
  // Convert to string for comparison since min/max come as strings from API
  const minStr = String(min);
  const maxStr = String(max);
  const skipRangeValidation = minStr === "0" && maxStr === "0";

  // Validate based on mode
  if (!isRangeMode) {
    // Single value mode
    const value = attribute.value || 0;
    const hasValue = value !== 0;

    if (!hasValue) return ""; // No error if no value entered yet

    if (skipRangeValidation) {
      // Only check for null values
      if (value === null || value === undefined) {
        return "Value is required";
      }
    } else {
      // Normal range validation
      const minNum = parseFloat(min);
      const maxNum = parseFloat(max);

      if (value < minNum || value > maxNum) {
        return `Value must be between ${displayMin} and ${max} ${currentUnit.trim()}`;
      }
    }
  } else {
    // Range mode
    const minValue = attribute.minValue || 0;
    const maxValue = attribute.maxValue || 0;
    const hasMinValue = minValue !== 0;
    const hasMaxValue = maxValue !== 0;

    if (!hasMinValue && !hasMaxValue) return ""; // No error if no values entered yet

    // If one value is entered, both must be entered
    if (hasMinValue && !hasMaxValue) {
      return "Please enter both min and max values";
    }

    if (!hasMinValue && hasMaxValue) {
      return "Please enter both min and max values";
    }

    if (skipRangeValidation) {
      // Only check for null values and min < max
      if (hasMinValue && (minValue === null || minValue === undefined)) {
        return "Min value is required";
      }
      if (hasMaxValue && (maxValue === null || maxValue === undefined)) {
        return "Max value is required";
      }
      if (hasMinValue && hasMaxValue && minValue >= maxValue) {
        return "Min value must be less than max value";
      }
    } else {
      // Normal range validation
      const minNum = parseFloat(min);
      const maxNum = parseFloat(max);

      if (hasMinValue && (minValue < minNum || minValue > maxNum)) {
        return `Min value must be between ${displayMin} and ${max} ${currentUnit.trim()}`;
      }

      if (hasMaxValue && (maxValue < minNum || maxValue > maxNum)) {
        return `Max value must be between ${displayMin} and ${max} ${currentUnit.trim()}`;
      }

      if (hasMinValue && hasMaxValue && minValue >= maxValue) {
        return "Min value must be less than max value";
      }
    }
  }

  return ""; // No errors
};

export default NumberAndRangeElement;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    width: "100%",
  },
  headerContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "16px",
    marginBottom: "8px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  inputContainer: {
    display: "flex",
    flexDirection: "row" as "row",
    gap: "8px",
    alignItems: "flex-end",
    "@media (max-width: 600px)": {
      flexDirection: "column" as "column",
      alignItems: "stretch",
    },
  },
  inputWrapper: {
    flex: 1,
    minWidth: 0,
  },
  rangeText: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  errorText: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.DANGER_GRAPH_DEFICIT,
  }),
};
